import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Info, ChevronUp, ChevronDown } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import {
  useAtividadesDisponiveisAuditoria, useCreateAtividadeAuditoria, useUpdateAtividadeAuditoria,
  useCreateCriterioAuditoria, useUpdateCriterioAuditoria, useUpdateOrdemAtividadesAuditoria,
  parseErroAuditoria,
  type AtividadeDisponivel,
} from "@/hooks/use-auditoria";
import type { AudCriterio } from "@shared/schema";

import { PageHeader } from "@/components/layout/page-header";
import { PageToolbar, PageSearch } from "@/components/layout/page-toolbar";
import { DataCard } from "@/components/data/data-card";
import {
  DataTable, DataTableHeader, DataTableBody, DataTableRow,
  DataTableHead, DataTableCell,
} from "@/components/data/data-table";
import { EmptyState, NadaEncontrado } from "@/components/data/empty-state";
import { PageSkeleton } from "@/components/data/page-skeleton";
import { combina } from "@/lib/busca";
// ─── Linha de um critério — nome inline, sem peso, sem material de treinamento ─

const COLUNAS_CRITERIOS = [null, "8rem"];
const COLUNAS_NAO_AUDITADAS = [null, "6rem"];

function CriterioInline({
  criterio, onRename, onRemove, disabled,
}: {
  criterio: AudCriterio;
  onRename: (nome: string) => void;
  onRemove: () => void;
  disabled: boolean;
}) {
  const [nome, setNome] = useState(criterio.nome);
  useEffect(() => { setNome(criterio.nome); }, [criterio.nome]);

  return (
    <DataTableRow>
      <DataTableCell>
        <Input
          className="h-8 border-0 shadow-none px-0 focus-visible:ring-1 text-sm"
          value={nome}
          disabled={disabled}
          onChange={e => setNome(e.target.value)}
          onBlur={() => {
            const v = nome.trim();
            if (v && v !== criterio.nome) onRename(v); else setNome(criterio.nome);
          }}
        />
      </DataTableCell>
      <DataTableCell className="text-right">
        <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-destructive" onClick={onRemove} disabled={disabled}>
          remover
        </Button>
      </DataTableCell>
    </DataTableRow>
  );
}

/**
 * A linha do critério NOVO: nasce em modo de edição, com foco no input, e só
 * persiste quando o usuário digita e sai do campo. Nome vazio nunca vai para
 * o banco — só some a linha. Isso evita o nome fixo "Novo critério" que
 * reservava o nome mesmo depois de o usuário remover, travando a criação
 * seguinte (ver migração 055).
 */
function NovoCriterioInline({ onSalvar, onCancelar, salvando }: {
  onSalvar: (nome: string) => void;
  onCancelar: () => void;
  salvando: boolean;
}) {
  const [nome, setNome] = useState("");

  const commit = () => {
    const v = nome.trim();
    if (v) onSalvar(v); else onCancelar();
  };

  return (
    <DataTableRow>
      <DataTableCell>
        <Input
          autoFocus
          className="h-8 border-0 shadow-none px-0 focus-visible:ring-1 text-sm"
          value={nome}
          disabled={salvando}
          placeholder="Nome do critério"
          onChange={e => setNome(e.target.value)}
          onBlur={commit}
          onKeyDown={e => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
        />
      </DataTableCell>
      <DataTableCell className="text-right" />
    </DataTableRow>
  );
}

// ─── Atividade auditada — critérios editáveis ──────────────────────────────────

function AtividadeAuditada({
  atividade, podeSubir, podeDescer, onSubir, onDescer, movendo,
}: {
  atividade: AtividadeDisponivel;
  podeSubir: boolean;
  podeDescer: boolean;
  onSubir: () => void;
  onDescer: () => void;
  movendo: boolean;
}) {
  const { toast } = useToast();
  const update = useUpdateAtividadeAuditoria();
  const createCriterio = useCreateCriterioAuditoria();
  const updateCriterio = useUpdateCriterioAuditoria();
  const [novoCriterioAberto, setNovoCriterioAberto] = useState(false);

  const desligar = () => {
    if (!atividade.audAtividadeId) return;
    update.mutate(
      { id: atividade.audAtividadeId, data: { ativo: false } },
      { onError: (e: any) => toast({ title: "Erro ao desligar", description: e?.message, variant: "destructive" }) },
    );
  };

  // Só abre a linha em branco — o POST só acontece quando o usuário digitar
  // e sair do campo (NovoCriterioInline). Nome fixo era o que reservava o
  // nome mesmo depois de removido (ver migração 055).
  const addCriterio = () => {
    if (!atividade.audAtividadeId) return;
    setNovoCriterioAberto(true);
  };

  const salvarNovoCriterio = (nome: string) => {
    if (!atividade.audAtividadeId) return;
    createCriterio.mutate(
      { atividadeId: atividade.audAtividadeId, data: { nome } },
      {
        onSuccess: () => setNovoCriterioAberto(false),
        onError: (e: any) => {
          const erro = parseErroAuditoria(e, "Erro ao criar critério");
          toast({ title: "Erro ao criar critério", description: erro.message, variant: "destructive" });
          // Mantém a linha aberta — o usuário não perde o que digitou e pode
          // corrigir o nome (o 409 aqui é sempre um nome ATIVO de verdade).
        },
      },
    );
  };

  const renomear = (id: string, nome: string) => {
    updateCriterio.mutate(
      { id, data: { nome } },
      { onError: (e: any) => toast({ title: "Erro ao renomear", description: e?.message, variant: "destructive" }) },
    );
  };

  const remover = (id: string) => {
    updateCriterio.mutate(
      { id, data: { ativo: false } },
      { onError: (e: any) => toast({ title: "Erro ao remover", description: e?.message, variant: "destructive" }) },
    );
  };

  return (
    <DataCard
      titulo={atividade.nome}
      meta={`${atividade.criterios.length} critério${atividade.criterios.length === 1 ? "" : "s"}`}
      acao={
        <div className="flex items-center gap-1">
          {/* Par de setas: 44×22 cada, 44×44 no conjunto — o alvo de toque do
              stepper é o par, não cada seta isolada. */}
          <div className="flex flex-col -my-1 shrink-0">
            <button
              type="button"
              className="h-[22px] w-11 flex items-center justify-center text-muted-foreground disabled:opacity-30"
              onClick={onSubir}
              disabled={!podeSubir || movendo}
              aria-label="Mover para cima"
            >
              <ChevronUp className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              className="h-[22px] w-11 flex items-center justify-center text-muted-foreground disabled:opacity-30"
              onClick={onDescer}
              disabled={!podeDescer || movendo}
              aria-label="Mover para baixo"
            >
              <ChevronDown className="h-3.5 w-3.5" />
            </button>
          </div>
          <Switch checked={true} onCheckedChange={desligar} disabled={update.isPending} />
        </div>
      }
      /* Atividade auditada sem critério não aparece na auditoria — é estado, e
         estado com consequência vai no slot de aviso, não numa linha âmbar solta. */
      aviso={
        atividade.criterios.length === 0
          ? "Sem critério ainda — esta atividade não vai aparecer na auditoria."
          : undefined
      }
      rodape={
        <Button
          variant="ghost" size="sm" className="h-7 text-xs"
          onClick={addCriterio} disabled={createCriterio.isPending || novoCriterioAberto}
        >
          <Plus className="h-3.5 w-3.5 mr-1" />Critério
        </Button>
      }
    >
      {(atividade.criterios.length > 0 || novoCriterioAberto) && (
        <DataTable colunas={COLUNAS_CRITERIOS}>
          <DataTableHeader>
            <DataTableRow>
              <DataTableHead>Critério</DataTableHead>
              <DataTableHead />
            </DataTableRow>
          </DataTableHeader>
          <DataTableBody>
            {atividade.criterios.map(c => (
              <CriterioInline
                key={c.id}
                criterio={c}
                disabled={updateCriterio.isPending}
                onRename={nome => renomear(c.id, nome)}
                onRemove={() => remover(c.id)}
              />
            ))}
            {novoCriterioAberto && (
              <NovoCriterioInline
                onSalvar={salvarNovoCriterio}
                onCancelar={() => setNovoCriterioAberto(false)}
                salvando={createCriterio.isPending}
              />
            )}
          </DataTableBody>
        </DataTable>
      )}
    </DataCard>
  );
}

// ─── Atividade não auditada — só o interruptor ─────────────────────────────────

function AtividadeNaoAuditada({ atividade }: { atividade: AtividadeDisponivel }) {
  const { toast } = useToast();
  const update = useUpdateAtividadeAuditoria();
  const create = useCreateAtividadeAuditoria();
  const pendente = update.isPending || create.isPending;

  const ligar = async () => {
    try {
      if (atividade.audAtividadeId) {
        await update.mutateAsync({ id: atividade.audAtividadeId, data: { ativo: true } });
      } else {
        await create.mutateAsync({ nome: atividade.nome, atividadeId: atividade.atividadeId, ativo: true });
      }
    } catch (e: any) {
      toast({ title: "Erro ao ligar auditoria", description: e?.message, variant: "destructive" });
    }
  };

  // Opacidade só no nome: esmaecer a linha inteira apagava junto o interruptor,
  // que é exatamente o controle usado pra ligar a auditoria desta atividade.
  return (
    <DataTableRow>
      <DataTableCell destaque className="truncate opacity-60">{atividade.nome}</DataTableCell>
      <DataTableCell className="text-right">
        <Switch checked={false} onCheckedChange={ligar} disabled={pendente} />
      </DataTableCell>
    </DataTableRow>
  );
}

// ─── Dialog: atividade só de auditoria ─────────────────────────────────────────

function NovaAtividadeSoAuditoriaDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const { toast } = useToast();
  const create = useCreateAtividadeAuditoria();
  const [nome, setNome] = useState("");

  useEffect(() => { if (open) setNome(""); }, [open]);

  const salvar = async () => {
    const v = nome.trim();
    if (!v) return;
    try {
      await create.mutateAsync({ nome: v, atividadeId: null, ativo: true });
      toast({ title: "Atividade criada!" });
      onOpenChange(false);
    } catch (e: any) {
      toast({
        title: e?.message?.includes("409") ? "Já existe atividade com esse nome" : "Erro ao criar atividade",
        description: e?.message?.includes("409") ? undefined : e?.message,
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>Atividade só de auditoria</DialogTitle></DialogHeader>
        <div className="space-y-2">
          <Input
            autoFocus
            value={nome}
            onChange={e => setNome(e.target.value)}
            placeholder="Ex.: Descapa, Destoca"
            onKeyDown={e => { if (e.key === "Enter") salvar(); }}
          />
          <p className="text-xs text-muted-foreground">
            Para algo que você audita mas não é tarefa de diária. Não entra no cadastro de atividades do Equipe.
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={salvar} disabled={!nome.trim() || create.isPending}>Criar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Página ───────────────────────────────────────────────────────────────────

export default function AuditoriaCriterios() {
  const { toast } = useToast();
  const { data: atividadesData, isLoading } = useAtividadesDisponiveisAuditoria();
  const updateOrdem = useUpdateOrdemAtividadesAuditoria();
  const [novaAtividadeOpen, setNovaAtividadeOpen] = useState(false);
  const [busca, setBusca] = useState("");

  const atividades = useMemo(() => atividadesData ?? [], [atividadesData]);
  // Já vem ordenada pelo servidor (ordem, depois nome) — a ordem de exibição
  // aqui é a mesma que aparece no lançamento da auditoria.
  const auditadas = useMemo(() => atividades.filter(a => a.auditar), [atividades]);
  const naoAuditadas = useMemo(() => atividades.filter(a => !a.auditar), [atividades]);
  const totalCriterios = useMemo(() => auditadas.reduce((s, a) => s + a.criterios.length, 0), [auditadas]);

  // Busca NO CLIENTE, sobre as duas listas já carregadas.
  const auditadasVisiveis = auditadas.filter(a => combina(busca, a.nome));
  const naoAuditadasVisiveis = naoAuditadas.filter(a => combina(busca, a.nome));

  const mover = (index: number, direcao: -1 | 1) => {
    const alvo = index + direcao;
    if (alvo < 0 || alvo >= auditadas.length) return;
    const nova = [...auditadas];
    [nova[index], nova[alvo]] = [nova[alvo], nova[index]];
    updateOrdem.mutate(
      nova.map((a, i) => ({ id: a.audAtividadeId as string, ordem: i + 1 })),
      { onError: (e: any) => toast({ title: "Erro ao reordenar", description: e?.message, variant: "destructive" }) },
    );
  };

  return (
    <div className="p-4 sm:p-6 space-y-4 pb-24">
      <PageHeader titulo="Critérios de auditoria" descricao="O que auditar em cada atividade" />

      <div className="rounded-md border border-info/30 bg-info-suave p-3 flex gap-2">
        <Info className="h-4 w-4 text-info shrink-0 mt-0.5" />
        <p className="text-xs text-info leading-relaxed">
          As atividades vêm do cadastro de atividades do Equipe. Ligue as que você audita e cadastre os critérios
          dentro de cada uma — o critério é quem recebe nota. Sem peso em lugar nenhum.
        </p>
      </div>

      <PageToolbar
        fim={<PageSearch valor={busca} onChange={setBusca} placeholder="Atividade" testId="input-busca-criterios" />}
      >
        <span />
      </PageToolbar>

      {isLoading ? (
        <PageSkeleton variante="tabela" linhas={5} />
      ) : atividades.length === 0 ? (
        <EmptyState
          titulo="Nenhuma atividade disponível"
          descricao="As atividades vêm do cadastro do Equipe — cadastre-as lá, ou crie uma atividade só de auditoria abaixo."
          acao={
            <Button variant="outline" onClick={() => setNovaAtividadeOpen(true)}>
              <Plus className="h-4 w-4 mr-1.5" />Atividade só de auditoria
            </Button>
          }
          testId="empty-criterios"
        />
      ) : auditadasVisiveis.length === 0 && naoAuditadasVisiveis.length === 0 ? (
        <NadaEncontrado termo={busca} />
      ) : (
        <>
          <div className="rounded-md border p-3 flex items-center justify-between">
            <span className="text-xs text-muted-foreground">
              {auditadas.length} de {atividades.length} atividades · {totalCriterios} critérios
            </span>
          </div>

          <div className="space-y-2">
            <h2 className="text-sm font-semibold">Auditadas</h2>
            {auditadasVisiveis.length === 0 ? (
              <EmptyState
                compacto
                titulo={busca.trim() ? "Nenhuma auditada com esse termo" : "Nenhuma atividade auditada ainda"}
                descricao={busca.trim() ? undefined : "Ligue o interruptor de uma atividade abaixo para começar a auditá-la."}
              />
            ) : (
              auditadasVisiveis.map((a) => {
                // O índice da SETA é o da lista inteira, não o da filtrada:
                // reordenar tem que mover na ordem real, não na aparente.
                const i = auditadas.findIndex(x => (x.audAtividadeId ?? x.atividadeId) === (a.audAtividadeId ?? a.atividadeId));
                return (
                  <AtividadeAuditada
                    key={a.audAtividadeId ?? a.atividadeId}
                    atividade={a}
                    podeSubir={i > 0}
                    podeDescer={i < auditadas.length - 1}
                    onSubir={() => mover(i, -1)}
                    onDescer={() => mover(i, 1)}
                    movendo={updateOrdem.isPending}
                  />
                );
              })
            )}
          </div>

          <div className="space-y-2">
            <h2 className="text-sm font-semibold">Não auditadas</h2>
            <p className="text-xs text-muted-foreground -mt-1">Do cadastro de atividades, mas fora da auditoria hoje.</p>
            {naoAuditadasVisiveis.length === 0 ? (
              <EmptyState
                compacto
                titulo={busca.trim() ? "Nenhuma fora da auditoria com esse termo" : "Todas as atividades já estão auditadas"}
              />
            ) : (
              <DataCard>
                <DataTable colunas={COLUNAS_NAO_AUDITADAS}>
                  <DataTableHeader>
                    <DataTableRow>
                      <DataTableHead>Atividade</DataTableHead>
                      <DataTableHead className="text-right">Auditar</DataTableHead>
                    </DataTableRow>
                  </DataTableHeader>
                  <DataTableBody>
                    {naoAuditadasVisiveis.map(a => (
                      <AtividadeNaoAuditada key={a.audAtividadeId ?? a.atividadeId} atividade={a} />
                    ))}
                  </DataTableBody>
                </DataTable>
              </DataCard>
            )}
          </div>

          <Button variant="outline" className="w-full" onClick={() => setNovaAtividadeOpen(true)}>
            <Plus className="h-4 w-4 mr-1.5" />Atividade só de auditoria
          </Button>
        </>
      )}

      <NovaAtividadeSoAuditoriaDialog open={novaAtividadeOpen} onOpenChange={setNovaAtividadeOpen} />
    </div>
  );
}
